import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { find, get } from 'lodash';
import { EI18nLanguageUI, useTranslation } from '@packages/utils';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import {
  TRegistrationFormDataUI,
  TEventRegisterDataUI,
  ERegistrantStatus,
  EEventAgeGroup,
  TChildnameFormListItem,
  // EEventAgeGroup,
} from 'data/types/events.types';
import {
  useGetRegistrationFreeSlotsQuery,
  useLazyGetRegistrationFreeSlotsQuery,
  useRegisterEventMutation,
  useGetRegistrationFormDataQuery,
  useGetEventsByIdQuery,
  useRegisterMultipleEventMutation,
} from 'redux/apiSlice/events.slice';
import { ELanguage, TValidateFieldsError } from 'data/types/generic.types';
import * as Sentry from '@sentry/react';
import { TSuccessModal } from '../../RegistrationModal/RegistrationModalForm/SuccessModal/SuccessModal.types';
import { registrationModalKey } from '../../RegistrationModal/RegistrationModal.data';

export const AGE_OPTIONS: {
  [key: string]: string;
} = {
  ZERO_TO_ONE: '0-1',
  ONE_TO_TWO: '1-2',
  TWO_TO_THREE: '2-3 ',
  TWO_TO_FIVE: '2-5',
  THREE_TO_FOUR: '3-4',
  FOUR_TO_FIVE: '4-5',
  THREE_TO_FIVE: '3-5',
};

export const NO_CHILD_INFORMATION_REQUIRED: string[] = [
  'LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS',
  'CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1',
  'CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2',
];

export const LANGUAGE_LABELS: {
  [key: string]: string;
} = {
  es: 'Spanish',
  en: 'English',
};

export const LANGUAGE_OPTIONS: {
  [key: string]: string;
} = {
  ENGLISH: 'ENGLISH',
  SPANISH: 'SPANISH',
};

// can use this EI18nLanguageUI as a key
export const LANGUAGE_TRANSLATE: {
  [key in EI18nLanguageUI]: string;
} = {
  [EI18nLanguageUI.ENGLISH]: 'ENGLISH',
  [EI18nLanguageUI.SPANISH]: 'SPANISH',
};

export const PART_OPTIONS: {
  [key: string]: string;
} = {
  STANDALONE: 'Standalone',
  PART_ONE: 'Part 1',
  PART_TWO: 'Part 2',
  PART_THREE: 'Part 3',
};

export const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
];

type AdultInformation = {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  ageGroup: EEventAgeGroup;
  additionalAdults?: {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
  }[];
};

type ChildrenInformation = {
  name: string;
  ageGroup: EEventAgeGroup;
  showWarning: boolean;
};

// recieveEmail is the notification-consent checkbox; it is sent as notificationDisabled
type TRegistrationValues = TEventRegisterDataUI & { recieveEmail?: boolean };

type TRegistrationContextType = {
  stepper: number;
  setStepper: React.Dispatch<React.SetStateAction<number>>;
  // To define registration types later
  registrationValues: any;
  setRegistrationValues: React.Dispatch<React.SetStateAction<any>>;
  eventDetails: any;
  setEventDetails: React.Dispatch<React.SetStateAction<any>>;
  childrenInformation: ChildrenInformation[];
  setChildrenInformation: React.Dispatch<
    React.SetStateAction<ChildrenInformation[]>
  >;
  adultsInformation: any[];
  setAdultsInformation: React.Dispatch<React.SetStateAction<any[]>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  eventId: string;
  openSuccessModal: TSuccessModal;
  setOpenSuccessModal: React.Dispatch<React.SetStateAction<TSuccessModal>>;
  isRegistrationLoading: boolean;
  afterModalClose: () => void;
  handleSubmit: () => Promise<void>;
  getRegistrationDataSuccess: boolean;
  handleClose: () => void;
  isCapacityFull: boolean;
  eventFormData: TRegistrationFormDataUI | undefined;
  openWaitlistConfirmModal: boolean;
  setOpenWaitlistConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedClassByDate: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  hasFreeSlot: boolean | undefined;
  englishGroupCapacities: AgeGroupCapacitiesType;
  setEnglishGroupCapacities: React.Dispatch<
    React.SetStateAction<AgeGroupCapacitiesType | undefined>
  >;
  spanishGroupCapacities: AgeGroupCapacitiesType;
  setSpanishGroupCapacities: React.Dispatch<
    React.SetStateAction<AgeGroupCapacitiesType | undefined>
  >;
  resetAllValues: () => void; // To reset all values
  resetValuesFromStepThreeOnwards: () => void;
};

export const RegistrationContext =
  createContext<TRegistrationContextType | null>(null);

type RegistrationContextProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  eventId: string;
  children: React.ReactNode;
};

export type AgeGroupCapacitiesType =
  | {
      [key: string]: {
        ageGroup: EEventAgeGroup;
        full: boolean;
        classId: string;
      };
    }
  | undefined;

declare const process: {
  env: {
    REACT_APP_RECAPTCHA_MAGIC_KEY: string;
  };
};

export const RegistrationContextProvider: React.FC<
  RegistrationContextProps
> = ({ open, setOpen, eventId, children }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [eventDetails, setEventDetails] = useState<
    TRegistrationFormDataUI | undefined
  >(undefined);
  const [stepper, setStepper] = useState(0);
  const [childrenInformation, setChildrenInformation] = useState<
    ChildrenInformation[] | []
  >([]);
  const [adultsInformation, setAdultsInformation] = useState<
    AdultInformation[] | []
  >([]);
  const [englishGroupCapacities, setEnglishGroupCapacities] =
    useState<AgeGroupCapacitiesType>(undefined);
  const [spanishGroupCapacities, setSpanishGroupCapacities] =
    useState<AgeGroupCapacitiesType>(undefined);
  const [registrationValues, setRegistrationValues] =
    useState<TRegistrationValues>({
      eventId: '',
      eventClassId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      childNames: [],
      childcareRequirements: [],
      recaptchaResponse: '',
      hasReadyBinder: null, // Assuming 'NO' is a valid value for TYesNoOptionType
      userLanguage: EI18nLanguageUI.ENGLISH, // Assuming ENGLISH is a valid enum value
      materialsLanguage: undefined, // Assuming ENGLISH is a valid enum value
      customQuestionAnswer: '',
      allowedTakePicture: 'NO', // Assuming 'NO' is a valid value for TYesNoOptionType
      acceptedTerms: false,
    });

  useEffect(() => {
    if (eventDetails?.languageOptions.length === 1) {
      setRegistrationValues((prev: any) => ({
        ...prev,
        userLanguage:
          EI18nLanguageUI[
            eventDetails.languageOptions[0]
              .value as keyof typeof EI18nLanguageUI
          ],
      }));
    }
  }, [eventDetails]);

  const resetAllValues = () => {
    setStepper(0);
    setEventDetails(undefined);
    setChildrenInformation([]);
    setAdultsInformation([]);
    setEnglishGroupCapacities(undefined);
    setSpanishGroupCapacities(undefined);
    setRegistrationValues({
      eventId: '',
      eventClassId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      childNames: [],
      childcareRequirements: [],
      recaptchaResponse: '',
      hasReadyBinder: null, // Assuming 'NO' is a valid value for TYesNoOptionType
      userLanguage: EI18nLanguageUI.ENGLISH, // Assuming ENGLISH is a valid enum value
      materialsLanguage: undefined, // Assuming ENGLISH is a valid enum value
      customQuestionAnswer: '',
      allowedTakePicture: 'NO', // Assuming 'NO' is a valid value for TYesNoOptionType
      acceptedTerms: false,
    });
  };

  const resetValuesFromStepThreeOnwards = () => {
    setChildrenInformation([]);
    setAdultsInformation([]);
    setRegistrationValues((prev: any) => {
      const updated = {
        ...prev,
        eventId: prev.eventId,
        childNames: [],
        childcareRequirements: [],
        userLanguage: prev.userLanguage,
        hasReadyBinder: prev.hasReadyBinder,
        customQuestionAnswer: prev.customQuestionAnswer,
        allowedTakePicture: prev.allowedTakePicture,
        acceptedTerms: prev.acceptedTerms,
      };
      return updated;
    });
  };

  const lang = registrationValues.userLanguage;
  const selectedLanguage = LANGUAGE_TRANSLATE[lang as EI18nLanguageUI];

  const { data: eventFormData, isSuccess: getRegistrationDataSuccess } =
    useGetRegistrationFormDataQuery({ eventId }, { skip: !open });
  // Fetch event detail to enrich form data with eventLanguage when missing
  const { data: eventDetailForLanguage } = useGetEventsByIdQuery(eventId, {
    skip: !open,
  });
  const mergedEventFormData = useMemo(() => {
    if (!eventFormData) return eventFormData;
    const eventLanguage = (eventFormData as any).eventLanguage
      ? (eventFormData as any).eventLanguage
      : (eventDetailForLanguage as any)?.eventLanguage;
    const locationName = (eventFormData as any).locationName
      ? (eventFormData as any).locationName
      : (eventDetailForLanguage as any)?.locationName;
    const locationAddress = (eventFormData as any).locationAddress
      ? (eventFormData as any).locationAddress
      : (eventDetailForLanguage as any)?.locationAddress;

    return {
      ...(eventFormData as any),
      ...(eventLanguage && { eventLanguage }),
      ...(locationName && { locationName }),
      ...(locationAddress && { locationAddress }),
    };
  }, [eventFormData, eventDetailForLanguage]);

  const [openWaitlistConfirmModal, setOpenWaitlistConfirmModal] =
    useState<boolean>(false);

  const { i18n } = useTranslation({
    keyPrefix: registrationModalKey,
    lng: 'translate',
  });

  const { showToastNotification } = useShowToastNotification();

  const [selectedClassByDate, setSelectedClassByDate] = useState<
    string | undefined
  >();
  const [isCapacityFull, setIsCapacityFull] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState<TSuccessModal>({
    open: false,
    emails: [],
    lng: EI18nLanguageUI.ENGLISH,
  });

  const { data: hasFreeSlot, refetch } = useGetRegistrationFreeSlotsQuery(
    { eventClassId: selectedClassByDate, eventId },
    {
      skip: !selectedClassByDate,
      refetchOnMountOrArgChange: true,
    },
  );

  const handleClose = () => {
    setOpen(false);
    resetAllValues();
  };

  useEffect(() => {
    if (open && !!selectedClassByDate) {
      refetch();
    }
  }, [open]);

  useEffect(() => {
    setIsCapacityFull(
      selectedClassByDate !== undefined &&
        hasFreeSlot !== undefined &&
        !hasFreeSlot,
    );
  }, [selectedClassByDate, hasFreeSlot]);

  const [getFreeSlots] = useLazyGetRegistrationFreeSlotsQuery();

  const [registerEvent, { isLoading: isRegistrationLoading }] =
    useRegisterEventMutation();
  const [registerMultipleEvent] = useRegisterMultipleEventMutation();

  const afterModalClose = () => {
    // form.resetFields();
    i18n.changeLanguage(EI18nLanguageUI.ENGLISH);
    setIsCapacityFull(false);
    setSelectedClassByDate(undefined);
  };

  const handleOpenSuccessModal = (
    emails: string[],
    registrantStatus: ERegistrantStatus | undefined,
    currentLng: EI18nLanguageUI,
  ) => {
    if (openWaitlistConfirmModal) {
      setOpenWaitlistConfirmModal(false);
    }
    setOpenSuccessModal({
      open: true,
      emails,
      registrantStatus,
      eventType: eventFormData?.eventType,
      lng: currentLng,
    });
  };

  const checkFreeSlots = async (
    hasFreeSlotOnPreviusCheck: boolean,
    classId: string,
  ) => {
    let continueToCallRegister = true;
    try {
      if (hasFreeSlotOnPreviusCheck) {
        const hasSlot = await getFreeSlots({
          eventClassId: classId,
          eventId,
        }).unwrap();
        if (hasSlot === false) {
          setOpenWaitlistConfirmModal(true);
          continueToCallRegister = false;
        }
      }
    } catch {
      // do nothing
    }
    return continueToCallRegister;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubmitOld = async (values: any) => {
    try {
      // const values = await form.validateFields();
      const continueToCallRegister = await checkFreeSlots(
        !isCapacityFull,
        values.classByDate,
      );
      if (continueToCallRegister) {
        const { status } = await registerEvent({
          eventClassId: values.classByDate,
          ...values,
          eventId,
        }).unwrap();
        handleOpenSuccessModal(
          [values.email],
          status,
          i18n.language as EI18nLanguageUI,
        );
        handleClose();
      }
    } catch (e) {
      if (!(e as TValidateFieldsError)?.errorFields) {
        let message: string | undefined = get(e, 'data.message');
        if (
          message === 'Registration is not allowed.' &&
          eventFormData?.parts?.length &&
          eventFormData.parts.every(part => part.isPassed)
        ) {
          message =
            'Looks like this event has already passed. Please reach out to the coordinator or the person who shared it with you to get the latest event date.';
        }
        showToastNotification({
          message,
          type: ENotificationType.ERROR,
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const MAGIC_RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_MAGIC_KEY || '';

    const currentCapacities =
      selectedLanguage === ELanguage.ENGLISH
        ? englishGroupCapacities
        : spanishGroupCapacities;

    const resolveClassId = (adult: AdultInformation): string | undefined => {
      const fromCapacities =
        currentCapacities &&
        (find(currentCapacities, { ageGroup: adult.ageGroup }) as
          | { classId: string }
          | undefined);
      if (fromCapacities?.classId) return fromCapacities.classId;

      const english = (eventDetails?.englishClasses || []).map(c => c[1]);
      const spanish = (eventDetails?.spanishClasses || []).map(c => c[1]);
      const pool = selectedLanguage === ELanguage.ENGLISH ? english : spanish;
      if (pool.length === 1) return pool[0].classId;
      if (pool.length > 1) {
        const notPassed = pool.find(c => !c.isAllSessionsPassed);
        return (notPassed || pool[0])?.classId;
      }

      const all = [...english, ...spanish];
      if (all.length === 1) return all[0].classId;

      const groupedClasses = (eventDetailForLanguage as any)?.classes || [];
      if (Array.isArray(groupedClasses) && groupedClasses.length) {
        const flatClasses = groupedClasses
          .flatMap((grp: any) => grp?.classes || [])
          .filter(Boolean);
        if (flatClasses.length) {
          const languageKey =
            selectedLanguage === ELanguage.ENGLISH ? 'ENGLISH' : 'SPANISH';
          const forLanguage = flatClasses.filter(
            (cls: any) => cls.language === languageKey,
          );
          const byAgeInLang = forLanguage.find(
            (cls: any) => cls.ageGroup === adult.ageGroup,
          );
          if (byAgeInLang?.eventClassId) return byAgeInLang.eventClassId;
          if (forLanguage.length === 1 && forLanguage[0]?.eventClassId) {
            return forLanguage[0].eventClassId;
          }
          if (forLanguage.length > 1) {
            const notPassed = forLanguage.find((cls: any) =>
              Array.isArray(cls.sessions)
                ? cls.sessions.some((s: any) => !s?.isPassed)
                : true,
            );
            if (notPassed?.eventClassId) return notPassed.eventClassId;
            return forLanguage[0]?.eventClassId;
          }
          if (flatClasses.length === 1 && flatClasses[0]?.eventClassId) {
            return flatClasses[0].eventClassId;
          }
          if (flatClasses.length > 1) {
            const notPassedOverall = flatClasses.find((cls: any) =>
              Array.isArray(cls.sessions)
                ? cls.sessions.some((s: any) => !s?.isPassed)
                : true,
            );
            if (notPassedOverall?.eventClassId)
              return notPassedOverall.eventClassId;
            return flatClasses[0]?.eventClassId;
          }
        }
      }

      return undefined;
    };

    try {
      const filteredAdults = adultsInformation.filter(
        adult => adult !== null,
      ) as AdultInformation[];

      const registrationDetails = filteredAdults.map(
        (adult: AdultInformation, index: number) => {
          const classId = resolveClassId(adult) || '';

          const childrenNames: TChildnameFormListItem[] = (() => {
            if (
              currentCapacities &&
              Object.keys(currentCapacities).length === 1
            ) {
              return childrenInformation.map(child => ({ name: child.name }));
            }
            return childrenInformation
              .filter(child => child.ageGroup === adult.ageGroup)
              .map(child => ({ name: child.name }));
          })();

          const payload: TEventRegisterDataUI = {
            hasReadyBinder: registrationValues.hasReadyBinder,
            eventId,
            eventClassId: classId,
            firstName: adult.firstName,
            lastName: adult.lastName,
            email: adult.email,
            phone: adult.mobile,
            childNames: childrenNames,
            materialsLanguage: registrationValues.materialsLanguage,
            allowedTakePicture: registrationValues.allowedTakePicture,
            acceptedTerms: registrationValues.acceptedTerms,
            notificationDisabled: !registrationValues.recieveEmail,
            recaptchaResponse:
              index === 0
                ? registrationValues.recaptchaResponse
                : MAGIC_RECAPTCHA_KEY,
            userLanguage: registrationValues.userLanguage || '',
          };

          if (registrationValues.customQuestionAnswer !== '') {
            payload.customQuestionAnswer =
              registrationValues.customQuestionAnswer;
          }

          if (adult.additionalAdults && adult.additionalAdults.length) {
            payload.additionalAdults = adult.additionalAdults.map(
              additionalAdult => ({
                firstName: additionalAdult.firstName,
                lastName: additionalAdult.lastName,
                email: additionalAdult.email,
                phone: additionalAdult.mobile,
              }),
            );
          }

          if (index === 0) {
            payload.childcareRequirements =
              registrationValues.childcareRequirements;
          }

          return payload;
        },
      );

      const response = await registerMultipleEvent({
        registrationDetails,
      }).unwrap();

      const emails = (filteredAdults || []).map(adult => adult.email);
      // The confirmation must not depend on the response body shape: an empty
      // list would otherwise close the modal without any feedback.
      const registrantStatus =
        response.find(({ status }) => status === ERegistrantStatus.WAIT_LIST)
          ?.status ?? response[0]?.status;
      handleOpenSuccessModal(
        emails,
        registrantStatus,
        i18n.language as EI18nLanguageUI,
      );

      handleClose();
    } catch (e) {
      Sentry.captureException(e);
      const errorMessage =
        '🚨 Registration Failed 🚨\nSomething went wrong on our end. Please try again later. If the issue persists, contact support.';
      showToastNotification({
        message: errorMessage,
        type: ENotificationType.ERROR,
      });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const memorizedState = useMemo(
    () => ({
      stepper,
      setStepper,
      registrationValues,
      setRegistrationValues,
      eventDetails,
      setEventDetails,
      childrenInformation,
      setChildrenInformation,
      adultsInformation,
      setAdultsInformation,
      open,
      setOpen,
      eventId,
      openSuccessModal,
      isRegistrationLoading,
      afterModalClose,
      handleSubmit,
      getRegistrationDataSuccess,
      isCapacityFull,
      handleClose,
      eventFormData: mergedEventFormData,
      setOpenSuccessModal,
      openWaitlistConfirmModal,
      setOpenWaitlistConfirmModal,
      setSelectedClassByDate,
      hasFreeSlot,
      englishGroupCapacities,
      setEnglishGroupCapacities,
      spanishGroupCapacities,
      setSpanishGroupCapacities,
      resetAllValues,
      resetValuesFromStepThreeOnwards,
    }),
    [
      stepper,
      openSuccessModal,
      englishGroupCapacities,
      spanishGroupCapacities,
      hasFreeSlot,
      selectedClassByDate,
      registrationValues,
      resetAllValues,
      resetValuesFromStepThreeOnwards,
    ],
  );

  return (
    <RegistrationContext.Provider value={memorizedState}>
      {children}
    </RegistrationContext.Provider>
  );
};

function useRegistrationContext() {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error(
      'useRegistrationContext must be used within a RegistrationContext',
    );
  }
  return context;
}

export default useRegistrationContext;
